import type { Product } from './types'
import { productImages, type ProductImage } from './imageMap'

/**
 * Product catalogue — the single central data source (CMS-ready for v1).
 *
 * Copy (descriptions, materials, features, applications) and certifications are
 * PROVISIONAL industry-standard placeholders to be verified against official
 * spec sheets and test reports before launch. Model numbers, categories,
 * subcategories and imagery are authoritative.
 *
 * Subcategory grouping (tig-mig / stick) follows the TF IMAGES folder structure.
 * `gallery`/`thumbnail` are resolved from the generated imageMap below, so image
 * data lives in exactly one place.
 */
type ProductInput = Omit<Product, 'id' | 'gallery' | 'thumbnail'>

const WELDING_CERTS = ['EN 388', 'EN 407', 'EN 12477', 'ANSI/ISEA 105']

const catalogue: ProductInput[] = [
  // ── Welding Gloves › TIG / MIG ───────────────────────────────────────────
  {
    slug: 'tf108', modelNumber: 'TF108', name: 'Premium TIG Welding Glove',
    category: 'welding-gloves', subCategory: 'tig-mig',
    shortDescription: 'Soft grade-A goatskin TIG glove for precision welding.',
    description:
      'A precision-first TIG glove crafted from supple grade-A goatskin for exceptional dexterity and feel, with a reinforced cuff for heat protection during fine welding work.',
    materials: ['Grade-A goatskin leather', 'Kevlar-stitched seams'],
    availableSizes: ['S', 'M', 'L', 'XL'],
    applications: ['TIG welding', 'Precision fabrication', 'Light assembly'],
    features: ['Ultra-soft goatskin for fine dexterity', 'Kevlar-stitched seams for durability', 'Reinforced heat-resistant cuff'],
    certifications: WELDING_CERTS,
  },
  {
    slug: 'tf111', modelNumber: 'TF111', name: 'MIG Welding Glove',
    category: 'welding-gloves', subCategory: 'tig-mig',
    shortDescription: 'Durable cowhide MIG glove with reinforced palm.',
    description:
      'Built for MIG work, this glove pairs full-grain cowhide with a reinforced palm and lined cuff to balance protection and control across long shifts.',
    materials: ['Full-grain cowhide leather', 'Cotton-lined cuff'],
    availableSizes: ['M', 'L', 'XL'],
    applications: ['MIG welding', 'General fabrication', 'Metal handling'],
    features: ['Reinforced palm for wear resistance', 'Cotton-lined cuff for comfort', 'Balanced protection and control'],
    certifications: WELDING_CERTS,
    featured: true,
  },
  {
    slug: 'tf114', modelNumber: 'TF114', name: 'Soft-Grip TIG Glove',
    category: 'welding-gloves', subCategory: 'tig-mig',
    shortDescription: 'Lightweight TIG glove tuned for maximum dexterity.',
    description:
      'A lightweight TIG glove engineered for tactile sensitivity, with a slim profile that keeps finger movement natural during detailed welding.',
    materials: ['Premium goatskin leather', 'Kevlar stitching'],
    availableSizes: ['S', 'M', 'L', 'XL'],
    applications: ['TIG welding', 'Detail work', 'Instrumentation'],
    features: ['Slim tactile profile', 'Natural finger movement', 'Kevlar stitching'],
    certifications: WELDING_CERTS,
  },
  {
    slug: 'tf119', modelNumber: 'TF119', name: 'Heavy-Duty MIG Glove',
    category: 'welding-gloves', subCategory: 'tig-mig',
    shortDescription: 'Reinforced MIG glove for demanding fabrication.',
    description:
      'A rugged MIG glove with reinforced thumb and palm for heavy fabrication, delivering heat resistance and grip where the work is toughest.',
    materials: ['Split cowhide leather', 'Heat-resistant lining'],
    availableSizes: ['M', 'L', 'XL'],
    applications: ['MIG welding', 'Heavy fabrication', 'Foundry support'],
    features: ['Reinforced thumb and palm', 'Heat-resistant lining', 'Rugged construction'],
    certifications: WELDING_CERTS,
  },
  {
    slug: 'tf120', modelNumber: 'TF120', name: 'Professional MIG Welding Glove',
    category: 'welding-gloves', subCategory: 'tig-mig',
    shortDescription: 'Flagship MIG glove balancing protection and control.',
    description:
      'Our flagship MIG glove combines a durable cowhide shell with an insulated lining and reinforced cuff — the everyday standard for professional welders.',
    materials: ['Full-grain cowhide', 'Foam-insulated lining'],
    availableSizes: ['M', 'L', 'XL', '2XL'],
    applications: ['MIG welding', 'Structural fabrication', 'Heavy industry'],
    features: ['Foam-insulated lining', 'Reinforced protective cuff', 'Professional-grade durability'],
    certifications: WELDING_CERTS,
    featured: true,
  },

  // ── Welding Gloves › Stick ───────────────────────────────────────────────
  {
    slug: 'tf121', modelNumber: 'TF121', name: 'Stick Welding Glove',
    category: 'welding-gloves', subCategory: 'stick',
    shortDescription: 'Insulated cowhide glove for stick (SMAW) welding.',
    description:
      'A robust stick-welding glove with heavy cowhide construction and full insulation to withstand the high heat and spatter of SMAW work.',
    materials: ['Heavy cowhide leather', 'Cotton fleece lining'],
    availableSizes: ['L', 'XL'],
    applications: ['Stick / SMAW welding', 'Heavy fabrication', 'Foundry'],
    features: ['Full fleece insulation', 'Heavy cowhide shell', 'High-heat protection'],
    certifications: WELDING_CERTS,
  },
  {
    slug: 'tf122', modelNumber: 'TF122', name: 'Heavy Stick Welding Glove',
    category: 'welding-gloves', subCategory: 'stick',
    shortDescription: 'Maximum-protection stick glove for extreme heat.',
    description:
      'Engineered for extreme heat exposure, this heavy stick glove adds an extended cuff and reinforced palm for maximum protection during prolonged arc work.',
    materials: ['Premium side-split cowhide', 'Insulated lining'],
    availableSizes: ['L', 'XL'],
    applications: ['Stick / SMAW welding', 'Forging', 'Furnace work'],
    features: ['Extended protective cuff', 'Reinforced palm', 'Extreme-heat rated'],
    certifications: WELDING_CERTS,
    featured: true,
  },
  {
    slug: 'tf123', modelNumber: 'TF123', name: 'Gauntlet Stick Welding Glove',
    category: 'welding-gloves', subCategory: 'stick',
    shortDescription: 'Split-leather stick glove with a long gauntlet cuff for arc protection.',
    description:
      'A classic gauntlet stick-welding glove in soft split cowhide, with a long straight cuff that shields the forearm from sparks and spatter during SMAW work. Fully lined for heat protection with a wing-thumb design for a natural grip on the electrode holder.',
    materials: ['Split cowhide leather', 'Cotton-lined interior'],
    availableSizes: ['L', 'XL'],
    applications: ['Stick / SMAW welding', 'General fabrication', 'Metal handling'],
    features: ['Long gauntlet cuff for forearm protection', 'Fully lined for heat resistance', 'Wing-thumb design for natural grip'],
    certifications: WELDING_CERTS,
  },
  {
    slug: 'tf128', modelNumber: 'TF128', name: 'All-Round Stick Glove',
    category: 'welding-gloves', subCategory: 'stick',
    shortDescription: 'Versatile stick glove for mixed welding tasks.',
    description:
      'A versatile stick-welding glove offering a balance of dexterity and durability for varied arc-welding and general workshop tasks.',
    materials: ['Split cowhide leather', 'Cotton lining'],
    availableSizes: ['M', 'L', 'XL'],
    applications: ['Stick / SMAW welding', 'General workshop', 'Metal handling'],
    features: ['Balanced dexterity and durability', 'Cotton-lined comfort', 'Versatile everyday use'],
    certifications: WELDING_CERTS,
  },
  {
    slug: 'tf129', modelNumber: 'TF129', name: 'Heavy-Duty Stick Glove',
    category: 'welding-gloves', subCategory: 'stick',
    shortDescription: 'Rugged glove for structural and pipe welding.',
    description:
      'A heavy-duty welding glove designed for structural and pipe work, with a reinforced palm and heat-resistant lining for demanding site conditions.',
    materials: ['Split cowhide leather', 'Para-aramid stitching'],
    availableSizes: ['L', 'XL', '2XL'],
    applications: ['Pipe welding', 'Structural steel', 'Oil & gas'],
    features: ['Reinforced palm', 'Para-aramid stitching', 'Built for site conditions'],
    certifications: WELDING_CERTS,
    featured: true,
  },
  {
    slug: 'tf130', modelNumber: 'TF130', name: 'Premium Stick Glove',
    category: 'welding-gloves', subCategory: 'stick',
    shortDescription: 'Clean-finish stick glove with soft leather palm.',
    description:
      'A premium stick-welding glove with a soft, clean-finish leather palm for control, finished with a reinforced cuff for reliable heat protection.',
    materials: ['Grade-A goatskin', 'Reinforced cuff'],
    availableSizes: ['S', 'M', 'L', 'XL'],
    applications: ['Stick / SMAW welding', 'Precision work', 'Fabrication'],
    features: ['Soft clean-finish palm', 'Precise control', 'Reinforced heat cuff'],
    certifications: WELDING_CERTS,
  },

  // ── Driver Gloves ────────────────────────────────────────────────────────
  {
    slug: 'tf222', modelNumber: 'TF222', name: 'Leather Driver Glove',
    category: 'driver-gloves',
    shortDescription: 'Classic cowhide driver glove with shirred wrist.',
    description:
      'A classic leather driver glove with a shirred elastic wrist for a secure fit, ideal for material handling, driving and general-purpose industrial work.',
    materials: ['Grain cowhide leather', 'Shirred elastic wrist'],
    availableSizes: ['M', 'L', 'XL'],
    applications: ['Material handling', 'Driving', 'General purpose'],
    features: ['Shirred elastic wrist for secure fit', 'Durable grain cowhide', 'All-purpose versatility'],
    certifications: ['EN 388', 'ANSI/ISEA 105'],
  },
  {
    slug: 'tf224', modelNumber: 'TF224', name: 'Premium Driver Glove',
    category: 'driver-gloves',
    shortDescription: 'Premium grain-leather driver glove for all-day comfort.',
    description:
      'A premium driver glove made from soft grain leather for all-day comfort and grip, suited to logistics, construction and workshop handling.',
    materials: ['Premium grain leather', 'Keystone thumb'],
    availableSizes: ['M', 'L', 'XL'],
    applications: ['Logistics', 'Construction', 'Automotive'],
    features: ['Soft grain leather', 'Keystone thumb for movement', 'All-day comfort'],
    certifications: ['EN 388', 'ANSI/ISEA 105'],
  },

  // ── General Purpose Gloves ───────────────────────────────────────────────
  {
    slug: 'tf402', modelNumber: 'TF402', name: 'Leather Palm Work Glove',
    category: 'general-purpose-gloves',
    shortDescription: 'Split-leather palm work glove with a striped cotton back and safety cuff.',
    description:
      'A general-purpose work glove built with a full split-leather palm and reinforced wing thumb for abrasion resistance and grip, a breathable striped cotton drill back to keep hands cool, and a rubberized safety cuff for a secure fit and quick removal. A dependable all-round glove for handling, construction and yard work.',
    materials: ['Split cowhide leather palm', 'Striped cotton drill back', 'Rubberized safety cuff'],
    availableSizes: ['M', 'L', 'XL'],
    applications: ['Construction', 'Material handling', 'Agriculture', 'General purpose'],
    features: [
      'Full split-leather palm with reinforced wing thumb',
      'Breathable striped cotton drill back',
      'Rubberized safety cuff for a secure fit and quick removal',
      'All-round durability for everyday handling work',
    ],
    certifications: ['EN 388', 'ANSI/ISEA 105'],
  },
  {
    slug: 'tf404', modelNumber: 'TF404', name: 'Cowhide Leather Work Glove',
    category: 'general-purpose-gloves',
    shortDescription: 'Split-leather palm work glove with a 2.5" rubberized safety cuff.',
    description:
      'A heavy-duty cowhide work glove for men and women, built with a reinforced double-layered split-leather palm for durability and heat resistance, a leather knuckle strap for added protection, and a breathable striped cotton back to keep hands cool and comfortable.',
    materials: ['Shoulder split cowhide leather', 'Striped cotton back', 'Leather knuckle strap'],
    availableSizes: ['M', 'L', 'XL'],
    applications: ['Construction', 'Material handling', 'General purpose'],
    features: [
      'Reinforced double-layered palm for heat resistance and durability',
      'Shoulder split cowhide leather for long-lasting toughness',
      '2.5" rubberized safety cuff for quick, easy removal',
      'Leather knuckle strap and breathable striped cotton back',
    ],
    certifications: ['EN 388', 'ANSI/ISEA 105'],
  },

  // ── Welding Caps ─────────────────────────────────────────────────────────
  {
    slug: 'tf601', modelNumber: 'TF601', name: 'Welding Cap',
    category: 'welding-caps',
    shortDescription: 'Reversible cotton welding cap with reflective stitching — five colours, one size fits most.',
    description:
      "A durable welder's cap made from 10–15 oz 100% cotton duck for breathable, all-day comfort under a helmet. Reversible printed fabric with a reflective luminous stitch pattern across the band and brim for low-light visibility, a 1.8\" band for a secure fit and a 3.5\" rear shade that shields the neck from sparks. One size fits most (56–64 cm / cap size 7–8), available in black, blue, brown, grey and maroon.",
    materials: ['10–15 oz 100% cotton duck fabric', 'Reflective luminous stitch thread'],
    availableSizes: ['One Size'],
    applications: ['Welding', 'Fabrication', 'Foundry', 'Low-light work areas'],
    features: [
      'Reflective luminous stitching on band and brim for low-light visibility',
      'Reversible printed 100% cotton, breathable under a helmet',
      '1.8" band for a secure fit; 3.5" rear shade protects the neck',
      'One size fits most (56–64 cm); five colour options',
    ],
    certifications: [],
    colors: [
      { name: 'Black', sku: 'tf601-black', swatch: '#1B1B1B' },
      { name: 'Blue', sku: 'tf601-blue', swatch: '#2C4A7A' },
      { name: 'Brown', sku: 'tf601-brown', swatch: '#6B4A2E' },
      { name: 'Grey', sku: 'tf601-grey', swatch: '#6E7377' },
      { name: 'Maroon', sku: 'tf601-mahroon', swatch: '#6E2233' },
    ],
    featured: true,
  },

  // ── Leather Garments & Accessories ───────────────────────────────────────
  {
    slug: 'tf501', modelNumber: 'TF501', name: 'Leather Welding Jacket',
    category: 'leather-garments',
    shortDescription: 'Full-coverage leather jacket for welding protection.',
    description:
      'A full-coverage leather welding jacket offering durable protection against sparks and spatter, with an ergonomic cut for freedom of movement.',
    materials: ['Split cowhide leather', 'Flame-resistant thread'],
    availableSizes: ['S', 'M', 'L', 'XL', '2XL'],
    applications: ['Welding & fabrication', 'Foundry', 'Heavy industry'],
    features: ['Full upper-body coverage', 'Flame-resistant thread', 'Ergonomic cut'],
    certifications: ['EN ISO 11611'],
    featured: true,
  },
  {
    slug: 'tf502', modelNumber: 'TF502', name: 'Welding Cape Sleeve & Bib',
    category: 'leather-garments',
    shortDescription: 'Cape sleeve with bib for upper-body protection.',
    description:
      'A leather cape sleeve with detachable bib that protects the shoulders, arms and chest during overhead and heavy welding.',
    materials: ['Split cowhide leather', 'Adjustable straps'],
    availableSizes: ['M', 'L', 'XL'],
    applications: ['Overhead welding', 'Heavy fabrication', 'Foundry'],
    features: ['Detachable bib', 'Adjustable straps', 'Shoulder and arm coverage'],
    certifications: ['EN ISO 11611'],
  },
  {
    slug: 'tf512', modelNumber: 'TF512', name: 'Electrode / Rod Holder Pouch',
    category: 'leather-garments',
    shortDescription: 'Leather pouch for welding electrodes and rods.',
    description:
      'A durable leather pouch that keeps welding electrodes and filler rods organised and within reach on the belt during work.',
    materials: ['Cowhide leather', 'Riveted seams'],
    availableSizes: ['One Size'],
    applications: ['Welding', 'Site work', 'Fabrication'],
    features: ['Belt-mounted convenience', 'Riveted heavy-duty seams', 'Keeps rods organised'],
    certifications: [],
  },
  {
    slug: 'tf513', modelNumber: 'TF513', name: 'Welding Cable Cover',
    category: 'leather-garments',
    shortDescription: 'Protective sheath for welding gun cables.',
    description:
      'A protective leather cover that shields welding gun and cable assemblies from heat, abrasion and spatter, with a hook-and-loop closure for easy fitting.',
    materials: ['Split leather', 'Hook-and-loop closure'],
    availableSizes: ['Standard'],
    applications: ['Cable protection', 'Welding', 'Workshop'],
    features: ['Heat and abrasion shielding', 'Hook-and-loop fitting', 'Extends cable life'],
    certifications: [],
  },
  {
    slug: 'tf516', modelNumber: 'TF516', name: 'Leather Hand Shield',
    category: 'leather-garments',
    shortDescription: 'Leather hand shield for spot protection.',
    description:
      'A lightweight leather hand shield that provides quick spot protection from heat and sparks during grinding and welding tasks.',
    materials: ['Cowhide leather', 'Spring-clip frame'],
    availableSizes: ['One Size'],
    applications: ['Welding', 'Grinding', 'Spot protection'],
    features: ['Quick spot protection', 'Lightweight frame', 'Heat and spark resistant'],
    certifications: ['EN ISO 11611'],
  },
  {
    slug: 'tf518', modelNumber: 'TF518', name: 'Leather Welding Sleeves',
    category: 'leather-garments',
    shortDescription: '18-inch leather sleeves with elastic cuffs.',
    description:
      'Durable 18-inch leather welding sleeves with an adjustable velcro strap and elastic cuff, protecting the arms from sparks and heat.',
    materials: ['Cowhide leather', 'Elastic cuff', 'Velcro strap'],
    availableSizes: ['18"'],
    applications: ['Welding', 'Fabrication', 'Metal handling'],
    features: ['18-inch arm coverage', 'Adjustable velcro strap', 'Elastic wrist cuff'],
    certifications: ['EN ISO 11611'],
  },
  {
    slug: 'tf520', modelNumber: 'TF520', name: 'Cotton-Leather Welding Sleeves',
    category: 'leather-garments',
    shortDescription: 'Breathable cotton-leather hybrid sleeves.',
    description:
      'Breathable hybrid sleeves combining a cotton body with leather reinforcement, offering lighter arm protection for lighter-duty welding.',
    materials: ['Flame-resistant cotton', 'Leather reinforcement'],
    availableSizes: ['18"'],
    applications: ['Light welding', 'Grinding', 'General workshop'],
    features: ['Breathable cotton body', 'Leather reinforcement', 'Lightweight protection'],
    certifications: ['EN ISO 11611'],
  },
]

/** Full product records with images resolved from the imageMap (single source). */
export const products: Product[] = catalogue.map((p) => {
  // Colour-variant products have no images under their own slug — default the
  // gallery/thumbnail to the first colour's imageMap key.
  const gallerySku = p.colors?.[0]?.sku ?? p.slug
  return {
    ...p,
    id: p.slug,
    gallery: productImages[gallerySku] ?? [],
    thumbnail: productImages[gallerySku]?.[0],
  }
})

// ── Accessors ───────────────────────────────────────────────────────────────

export const productImagesFor = (slug: string): ProductImage[] => productImages[slug] ?? []

export const getProduct = (slug?: string): Product | undefined =>
  products.find((p) => p.slug === slug)

export const productsByCategory = (categorySlug: string): Product[] =>
  products.filter((p) => p.category === categorySlug)

export const productsBySubCategory = (categorySlug: string, subSlug: string): Product[] =>
  products.filter((p) => p.category === categorySlug && p.subCategory === subSlug)

export const featuredProducts = (limit = 4): Product[] =>
  products.filter((p) => p.featured).slice(0, limit)

export const relatedProducts = (product: Product, limit = 4): Product[] =>
  products
    .filter((p) => p.category === product.category && p.slug !== product.slug)
    .slice(0, limit)

/** First available image across a category — used for category cards. */
export const categoryPrimaryImage = (categorySlug: string): ProductImage | undefined =>
  productsByCategory(categorySlug)[0]?.thumbnail

/** First available image within a subcategory — used for subcategory cards. */
export const subCategoryPrimaryImage = (categorySlug: string, subSlug: string): ProductImage | undefined =>
  productsBySubCategory(categorySlug, subSlug)[0]?.thumbnail
